'use client';

import type { HandleChangeAnswerType } from '~/components/Interaction/Form/Question/model';
import type { AnswerChoicesType, AnswerUserType } from '~/model/AnswerUserType';
import type { ChoiceType } from '~/model/ChoiceType';
import { VpFormControl, VpFormLabel, VpTextarea } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { MainColumn } from '~/components/commons/MainColumn/MainColumn';
import {
  checkIfHasPictureChoice,
  getPicturesList,
  shuffleArray,
} from '~/components/Interaction/Form/Question/utils';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { apiTypes } from '~/const/api';
import { CheckTypeQuestion } from './Check';
import { PicturesGallery, usePicturesGallery } from './PicturesGallery';
import { RadioTypeQuestion } from './Radio';

export const ChoiceTypeQuestion = ({
  answerUser,
  answerChoices,
  questionId,
  handleChangeAnswer,
  type,
  other_answer,
  choices_random_order,
  langValue,
  disabled = false,
}: ChoiceTypeQuestionProps) => {
  const t = useTranslations('FORM');

  const [choices, setChoices] = useState(() => choices_random_order ? shuffleArray(answerChoices) : answerChoices);
  const hasPictureChoice = checkIfHasPictureChoice(answerChoices);
  const picturesList = getPicturesList(answerChoices);
  const picturesListGallery = picturesList?.map(uri =>
    getResizedPictureUrl(uri, '1600x0'),
  );

  const { isOpen, setIsOpen, photoIndex, setPhotoIndex, openGallery }
    = usePicturesGallery(picturesListGallery);

  const nbChoices = choices?.length;
  const hasLessThanThreeChoices = nbChoices < 3;
  const shouldUseCenteredLayout
    = !hasPictureChoice || (hasPictureChoice && hasLessThanThreeChoices);

  const isRadioLikeType
    = type === apiTypes.RADIO || type === apiTypes.CONDITIONAL;
  const typeClass = isRadioLikeType ? 'control-radios' : 'control-checkboxes';
  const layoutClass = hasPictureChoice ? 'control-inline' : 'control-stacked';
  const countClass = hasPictureChoice ? `${typeClass}--${nbChoices}` : '';

  useEffect(() => {
    if (choices_random_order)
      setChoices(shuffleArray(answerChoices));
    else
      setChoices(answerChoices);
  }, []);

  return (
    <>
      <PicturesGallery
        key={`gallery-${questionId}`}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        photoIndex={photoIndex}
        setPhotoIndex={setPhotoIndex}
        picturesList={picturesListGallery}
        picturesListFallback={picturesList}
      />
      <MainColumn
        column={shouldUseCenteredLayout ? journeyColumnTemplate : undefined}
      >
        <div
          data-cy="choices"
          data-testid="choices"
          className={classNames([typeClass], [layoutClass], [countClass])}
        >
          {type === apiTypes.RADIO
            || type === apiTypes.CONDITIONAL
            || type === apiTypes.CHOICES_JAR
            ? (
                <RadioTypeQuestion
                  answerUser={answerUser}
                  answerChoices={choices}
                  questionId={questionId}
                  handleChangeAnswer={handleChangeAnswer}
                  type={type}
                  openGallery={openGallery}
                  langValue={langValue}
                  disabled={disabled}
                />
              )
            : (
                <CheckTypeQuestion
                  answerUser={answerUser}
                  answerChoices={choices}
                  questionId={questionId}
                  handleChangeAnswer={handleChangeAnswer}
                  type={type}
                  openGallery={openGallery}
                  langValue={langValue}
                  disabled={disabled}
                />
              )}
        </div>
      </MainColumn>
      {other_answer && (
        <MainColumn column={journeyColumnTemplate}>
          <div className="other-answer">
            <VpFormControl>
              <VpFormLabel>
                {t('OTHER_ANSWER')}
              </VpFormLabel>
              <VpTextarea
                id={`other-answer-${questionId}`}
                aria-label={t('PLACEHOLDER_TEXT')}
                data-testid="other-answer"
                disabled={disabled}
                placeholder={t('PLACEHOLDER_TEXT')}
                className="other-answer__input tc_recruitment_answer_text"
                value={!(type === apiTypes.RADIO
                  && (answerUser[questionId]?.answer_choices as AnswerChoicesType)?.length)
                && answerUser[questionId]?.value_text
                && answerUser[questionId].value_text != null
                  ? answerUser[questionId].value_text
                  : ''}
                onChange={e => handleChangeAnswer(
                  e.target.value,
                  questionId,
                  type,
                  undefined,
                  true,
                )}
              />
            </VpFormControl>
          </div>
        </MainColumn>
      )}
    </>
  );
};

export interface ChoiceTypeQuestionProps {
  answerUser: AnswerUserType;
  answerChoices: ChoiceType[];
  questionId: string;
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  other_answer: boolean;
  choices_random_order: boolean;
  langValue: string;
  disabled?: boolean;
}
